import React, { useState, useEffect, useRef } from "react";
import {
    blockLocationAutofillProps,
    isUserInputEvent,
    revertUntrustedInput,
} from "../lib/blockBrowserAutofill";

const toFloatOrNull = (rawValue) => {
    if (rawValue === null || rawValue === undefined || rawValue === "") {
        return null;
    }
    const parsed = parseFloat(rawValue);
    return Number.isFinite(parsed) ? parsed : null;
};

/**
 * A saved place the map can use. Coordinates of 0, 0 mean the location was left empty.
 * @param {string} display
 * @param {number | null} lat
 * @param {number | null} lon
 * @returns {boolean}
 */
function isMappedPlace(display, lat, lon) {
    return Boolean(display && display.trim())
        && Number.isFinite(lat)
        && Number.isFinite(lon)
        && !(lat === 0 && lon === 0);
}

/**
 * @param {{ value?: string, lat?: number | null, lon?: number | null } | null | undefined} value
 * @returns {{ display: string, lat: number, lon: number } | null}
 */
function readPlace(value) {
    const display = (value?.value || "").trim();
    const lat = toFloatOrNull(value?.lat);
    const lon = toFloatOrNull(value?.lon);
    if (!isMappedPlace(display, lat, lon)) return null;
    return { display, lat, lon };
}

const LocationSearch = ({
    value,
    onChange,
    placeholder = "Search for a city...",
    searchType = "city", // "city" or "address"
    required = false,
    inputClassName = "",
    id,
    inputRef,
    selectTextOnClick = false,
    autoFocus = false,
    dataComposerAutofocus = false,
}) => {
    const initialPlace = readPlace(value);
    const [searchQuery, setSearchQuery] = useState(initialPlace?.display || "");
    const [cityResults, setCityResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(initialPlace);
    const dropdownRef = useRef(null);
    const [typingEnabled, setTypingEnabled] = useState(false);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const placeDisplay = (value?.value || "").trim();
    const placeLat = value?.lat;
    const placeLon = value?.lon;

    useEffect(() => {
        const place = readPlace({ value: placeDisplay, lat: placeLat, lon: placeLon });
        setSelected(place);
        if (!place && placeDisplay) {
            onChangeRef.current({ value: "", lat: null, lon: null });
            setSearchQuery("");
            return;
        }
        setSearchQuery(place?.display || "");
    }, [placeDisplay, placeLat, placeLon]);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        }

        if (showDropdown) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showDropdown]);

    const matchesSelection = Boolean(selected)
        && searchQuery.trim().toLowerCase() === selected.display.toLowerCase();

    // Debounced city/address search using Nominatim API
    useEffect(() => {
        if (!searchQuery.trim() || searchQuery.length < 2 || matchesSelection) {
            setCityResults([]);
            return;
        }

        const timeoutId = setTimeout(async () => {
            setLoading(true);
            try {
                // Use backend proxy to avoid CORS issues
                const url = `/api/search/location/?q=${encodeURIComponent(searchQuery)}&searchType=${searchType}`;
                
                const response = await fetch(url);
                
                if (response.ok) {
                    const result = await response.json();
                    const data = result.data || [];
                    
                    let formattedResults;
                    
                    if (searchType === "address") {
                        // Format results as full addresses
                        // Filter to only include results with street/road or house number (actual addresses, not just cities)
                        formattedResults = data
                            .filter((place) => {
                                const address = place.address || {};
                                const houseNumber = address.house_number || '';
                                const road = address.road || address.street || '';
                                // Only include if it has a house number or road (actual address)
                                return houseNumber || road;
                            })
                            .map((place) => {
                                const address = place.address || {};
                                const houseNumber = address.house_number || '';
                                const road = address.road || address.street || '';
                                const city = address.city || address.town || address.village || '';
                                const state = address.state || address.region || '';
                                const postcode = address.postcode || '';
                                const country = address.country || '';
                                
                                // Build full address
                                let displayName = '';
                                if (houseNumber && road) {
                                    displayName = `${houseNumber} ${road}`;
                                } else if (road) {
                                    displayName = road;
                                } else if (houseNumber) {
                                    displayName = houseNumber;
                                }
                                
                                // Add city, state, postal code, country
                                const locationParts = [];
                                if (city) locationParts.push(city);
                                if (state) locationParts.push(state);
                                if (postcode) locationParts.push(postcode);
                                if (country && country !== state) locationParts.push(country);
                                
                                if (locationParts.length > 0) {
                                    if (displayName) {
                                        displayName += `, ${locationParts.join(', ')}`;
                                    } else {
                                        displayName = locationParts.join(', ');
                                    }
                                }
                                
                                // Fallback to display_name if we couldn't build a good address
                                if (!displayName || displayName.trim() === '') {
                                    displayName = place.display_name;
                                }
                                
                                return {
                                    display: displayName,
                                    fullAddress: place.display_name,
                                    houseNumber: houseNumber,
                                    road: road,
                                    city: city,
                                    state: state,
                                    postcode: postcode,
                                    country: country,
                                    lat: toFloatOrNull(place.lat),
                                    lon: toFloatOrNull(place.lon)
                                };
                            });
                    } else {
                        // Format results as "City, State/Province, Country"
                        formattedResults = data.map((place) => {
                            const address = place.address || {};
                            const city = address.city || address.town || address.village || place.display_name.split(',')[0];
                            const state = address.state || address.region || '';
                            const country = address.country || '';
                            
                            let displayName = city;
                            if (state) displayName += `, ${state}`;
                            if (country && country !== state) displayName += `, ${country}`;
                            
                            return {
                                display: displayName,
                                city: city,
                                state: state,
                                country: country,
                                fullAddress: place.display_name,
                                lat: toFloatOrNull(place.lat),
                                lon: toFloatOrNull(place.lon)
                            };
                        });
                    }
                    
                    // Remove duplicates based on display name (case-insensitive)
                    const uniqueResults = [];
                    const seenDisplayNames = new Set();
                    
                    for (const result of formattedResults) {
                        const displayLower = result.display.toLowerCase();
                        if (seenDisplayNames.has(displayLower)) continue;
                        if (!isMappedPlace(result.display, result.lat, result.lon)) continue;
                        seenDisplayNames.add(displayLower);
                        uniqueResults.push(result);
                    }
                    
                    setCityResults(uniqueResults);
                } else {
                    setCityResults([]);
                }
            } catch (error) {
                console.error("Location search error:", error);
                setCityResults([]);
            } finally {
                setLoading(false);
            }
        }, 500); // 500ms debounce

        return () => clearTimeout(timeoutId);
    }, [searchQuery, searchType, matchesSelection]);

    /**
     * @param {{ display: string, lat: number | null, lon: number | null }} city
     * @returns {void}
     */
    const handleCitySelect = (city) => {
        const lat = toFloatOrNull(city.lat);
        const lon = toFloatOrNull(city.lon);
        if (!isMappedPlace(city.display, lat, lon)) return;
        const locationData = {
            value: city.display,
            lat,
            lon,
        };
        onChange(locationData);
        setSelected({ display: city.display, lat, lon });
        setSearchQuery(city.display);
        setShowDropdown(false);
        setCityResults([]);
    };

    const handleInputChange = (e) => {
        if (!isUserInputEvent(e)) {
            revertUntrustedInput(e, searchQuery);
            return;
        }
        const newValue = e.target.value;
        setSearchQuery(newValue);
        setShowDropdown(true);
        if (!newValue) {
            setSelected(null);
            onChange({ value: "", lat: null, lon: null });
        }
    };

    const handleBlur = () => {
        const trimmedQuery = searchQuery.trim();
        const exactMatch = cityResults.find(
            (city) => city.display.toLowerCase() === trimmedQuery.toLowerCase()
                && isMappedPlace(city.display, toFloatOrNull(city.lat), toFloatOrNull(city.lon))
        );

        if (exactMatch) {
            handleCitySelect(exactMatch);
            return;
        }

        if (selected && trimmedQuery.toLowerCase() === selected.display.toLowerCase()) {
            setSearchQuery(selected.display);
            setShowDropdown(false);
            setCityResults([]);
            return;
        }

        setSearchQuery(selected?.display || "");
        setShowDropdown(false);
        setCityResults([]);
    };

    /**
     * @param {React.KeyboardEvent<HTMLInputElement>} event
     * @returns {void}
     */
    const handleKeyDown = (event) => {
        if (event.key === "Enter" && cityResults.length > 0) {
            event.preventDefault();
            handleCitySelect(cityResults[0]);
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <input
                id={id}
                ref={inputRef}
                type="text"
                name="place-query"
                placeholder={placeholder}
                value={searchQuery}
                readOnly={!typingEnabled}
                autoFocus={autoFocus}
                data-composer-autofocus={dataComposerAutofocus ? "" : undefined}
                {...blockLocationAutofillProps}
                onChange={handleInputChange}
                onClick={(event) => {
                    if (!selectTextOnClick) return;
                    if (searchQuery !== (selected?.display || "")) return;
                    const input = event.currentTarget;
                    requestAnimationFrame(() => input.select());
                }}
                onFocus={(event) => {
                    event.currentTarget.readOnly = false;
                    event.currentTarget.setAttribute("autocomplete", "new-password");
                    setTypingEnabled(true);
                    if (matchesSelection) return;
                    if (cityResults.length > 0 || searchQuery.length >= 2) {
                        setShowDropdown(true);
                    }
                }}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                required={required}
                className={`bg-secondary text-text-primary w-full border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent ${inputClassName}`}
            />
            {loading && (
                <div className="absolute right-3 top-2.5 text-text-secondary">
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </div>
            )}
            {showDropdown && cityResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-secondary border border-border rounded shadow-lg max-h-60 overflow-auto list-scrollbar">
                    {cityResults.map((city, index) => (
                        <button
                            key={index}
                            type="button"
                            onMouseDown={(e) => {
                                e.preventDefault(); // Prevent input blur
                                handleCitySelect(city);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-tertiary text-text-primary transition"
                        >
                            {city.display}
                        </button>
                    ))}
                </div>
            )}
            {showDropdown && searchQuery.length >= 2 && !loading && cityResults.length === 0 && (
                <div className="absolute z-10 w-full mt-1 bg-secondary border border-border rounded shadow-lg px-4 py-2 text-text-secondary text-sm">
                    {searchType === "address" ? "No addresses found. Try a different search." : "No cities found. Try a different search."}
                </div>
            )}
        </div>
    );
};

export default LocationSearch;
